import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import "./FinancePage.css";

interface BalanceItem {
  type: string;
  label: string;
  maid_id: number;
  maid_name: string;
  reminder_type?: string;
  due_date?: string;
  charge: number;
  paid: number;
  outstanding: number;
}

interface ClientBalance {
  client_id: number;
  client_name: string;
  contact_info: string;
  client_total: number;
  balances: BalanceItem[];
}

interface OutstandingBalancesResponse {
  status: string;
  total_outstanding: number;
  clients: ClientBalance[];
}

interface FinanceSummaryResponse {
  status: string;
  revenue: number;
  net_cost: number;
  profit: number;
  payments_collected: number;
}

interface MaidGroup {
  maid_id: number;
  maid_name: string;
  items: BalanceItem[];
}

const formatMoney = (amount: number) => {
  return `$${Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const localDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const FinancePage: React.FC = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState<ClientBalance[]>([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const today = new Date();

  const [fromDate, setFromDate] = useState(() =>
    localDate(new Date(today.getFullYear(), today.getMonth(), 1))
  );

  const [toDate, setToDate] = useState(() => localDate(today));

  const [summary, setSummary] = useState<FinanceSummaryResponse | null>(null);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  // ============================================================
  // GET AUTHENTICATION TOKEN
  // ============================================================

  const getToken = (): string => {
    const persisted = JSON.parse(localStorage.getItem("persist:root") || "{}");

    const user = JSON.parse(persisted.user || "{}");

    if (!user.token) {
      throw new Error("Authentication token not found");
    }

    return user.token;
  };

  // ============================================================
  // LOAD CURRENT OUTSTANDING BALANCES
  // NOT AFFECTED BY DATE FILTER
  // ============================================================

  const loadOutstandingBalances = async () => {
    try {
      setLoading(true);
      setError("");

      const token = getToken();

      const response = await api.get<OutstandingBalancesResponse>(
        "/outstanding-balances",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setClients(response.data.clients || []);

      setTotalOutstanding(response.data.total_outstanding || 0);
    } catch (err: any) {
      console.error("Failed to load outstanding balances:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load outstanding balances."
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // LOAD DATE-FILTERED FINANCIAL SUMMARY
  // ============================================================

  const loadSummary = async (start: string, end: string) => {
    if (!start || !end || start > end) {
      setSummaryError("Choose a valid date range. From must not be after To.");
      return;
    }

    try {
      setSummaryLoading(true);
      setSummaryError("");
      setSummary(null);

      const token = getToken();

      const response = await api.get<FinanceSummaryResponse>(
        "/finance-summary",
        {
          params: {
            from_date: start,
            to_date: end,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSummary(response.data);
    } catch (err: any) {
      console.error("Failed to load financial summary:", err);

      setSummaryError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load financial summary."
      );
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadOutstandingBalances();
    loadSummary(fromDate, toDate);

    // Initial load only.
    // Date changes require clicking Apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // GROUP OUTSTANDING BALANCES BY MAID
  // ============================================================

  const groupBalancesByMaid = (balances: BalanceItem[]): MaidGroup[] => {
    const grouped: Record<number, MaidGroup> = {};

    balances.forEach((balance) => {
      if (!grouped[balance.maid_id]) {
        grouped[balance.maid_id] = {
          maid_id: balance.maid_id,
          maid_name: balance.maid_name,
          items: [],
        };
      }

      grouped[balance.maid_id].items.push(balance);
    });

    return Object.values(grouped);
  };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="finance-page">
      <div className="finance-container">
        <div className="finance-header">
          <div>
            <h1>Finance</h1>
            <p>Financial performance and outstanding client balances</p>
          </div>
        </div>

        {/* ================================================
            TOTAL OUTSTANDING — ALWAYS CURRENT
        ================================================ */}

        <section className="finance-total-card">
          <div className="finance-total-label">
            Total Outstanding — All Clients
          </div>

          <div className="finance-total-amount">
            {formatMoney(totalOutstanding)}
          </div>

          <p>Current balance — not affected by dates</p>
        </section>

        {/* ================================================
            FINANCIAL SUMMARY
        ================================================ */}

        <section
          className="finance-summary-panel"
          style={{
            marginTop: 24,
            marginBottom: 32,
          }}
        >
          <h2>Financial Summary</h2>

          <p>
            Revenue, Net Cost and Profit are based on business dates. Payments
            Collected uses actual payment dates.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "end",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <label>
              From
              <input
                aria-label="From date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>

            <label>
              To
              <input
                aria-label="To date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>

            <button
              type="button"
              disabled={summaryLoading}
              onClick={() => loadSummary(fromDate, toDate)}
            >
              {summaryLoading ? "Loading..." : "Apply"}
            </button>
          </div>

          {summaryError && (
            <div className="finance-error" role="alert">
              {summaryError}
            </div>
          )}

          {summaryLoading && <p>Loading financial summary...</p>}

          {summary && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              {(
                [
                  ["Revenue", summary.revenue],
                  ["Net Cost", summary.net_cost],
                  ["Profit", summary.profit],
                  ["Payments Collected", summary.payments_collected],
                ] as [string, number][]
              ).map(([label, amount]) => (
                <div
                  key={label}
                  className="finance-client-card"
                  style={{ padding: 18 }}
                >
                  <div className="finance-total-label">{label}</div>

                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      marginTop: 8,
                    }}
                  >
                    {formatMoney(amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ================================================
            OUTSTANDING CLIENT BALANCES
        ================================================ */}

        <h2>Outstanding Balances</h2>

        {error && (
          <div className="finance-error">
            <div>{error}</div>

            <button type="button" onClick={loadOutstandingBalances}>
              Try Again
            </button>
          </div>
        )}

        {loading && <p>Loading outstanding balances...</p>}

        {!loading && !error && clients.length === 0 && (
          <div className="finance-empty">
            <h2>No Outstanding Balances</h2>
            <p>No current outstanding client balances.</p>
          </div>
        )}

        {!loading &&
          !error &&
          clients.map((client) => {
            const maidGroups = groupBalancesByMaid(client.balances);

            return (
              <section className="finance-client-card" key={client.client_id}>
                <div className="finance-client-header">
                  <button
                    type="button"
                    className="finance-client-name"
                    onClick={() => navigate(`/edit-client/${client.client_id}`)}
                  >
                    {client.client_name}
                  </button>

                  <div className="finance-client-contact">
                    {client.contact_info || "—"}
                  </div>
                </div>

                <div className="finance-divider" />

                {maidGroups.map((maid) => {
                  const maidDue = maid.items
                    .filter((item) => item.type === "maid_balance")
                    .reduce(
                      (sum, item) => sum + Number(item.outstanding || 0),
                      0
                    );

                  const reminderDue = maid.items
                    .filter((item) => item.type !== "maid_balance")
                    .reduce(
                      (sum, item) => sum + Number(item.outstanding || 0),
                      0
                    );

                  return (
                    <div className="finance-maid-section" key={maid.maid_id}>
                      <h3>{maid.maid_name}</h3>

                      <div className="finance-balance-list">
                        {maidDue > 0 && (
                          <div className="finance-balance-row">
                            <div className="finance-balance-label">
                              Maid Balance Due
                            </div>

                            <div className="finance-balance-amount">
                              {formatMoney(maidDue)}
                            </div>
                          </div>
                        )}

                        {reminderDue > 0 && (
                          <div className="finance-balance-row">
                            <div className="finance-balance-label">
                              Reminder Balance
                            </div>

                            <div className="finance-balance-amount">
                              {formatMoney(reminderDue)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div className="finance-client-total">
                  <span>Client Total</span>

                  <strong>{formatMoney(client.client_total)}</strong>
                </div>
              </section>
            );
          })}
      </div>
    </div>
  );
};

export default FinancePage;
